import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Shared icon medallion for page, section, and card headers.
 *
 * Variants:
 *   - `primary` — section/card header icon medallion.
 *   - `blue` — page-level icon medallion.
 *
 * Sizes:
 *   - `md` — 40px section/card icon.
 *   - `lg` — 48px page icon.
 */
const headerIconVariants = cva("cw-v2-header-icon", {
  variants: {
    variant: {
      primary: "cw-v2-header-icon--primary",
      blue: "cw-v2-header-icon--blue",
    },
    size: {
      md: "cw-v2-header-icon--md",
      lg: "cw-v2-header-icon--lg",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
  },
});

export type HeaderIconVariant = NonNullable<VariantProps<typeof headerIconVariants>["variant"]>;
export type HeaderIconSize = NonNullable<VariantProps<typeof headerIconVariants>["size"]>;

export interface HeaderIconProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof headerIconVariants> {
  icon?: React.ReactNode;
}

export const HeaderIcon = React.forwardRef<HTMLDivElement, HeaderIconProps>(function HeaderIcon(
  { className, icon, size, variant, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden
      className={cx(headerIconVariants({ variant, size }), className)}
      {...props}
    >
      {icon}
    </div>
  );
});
HeaderIcon.displayName = "HeaderIcon";
